import React, { useEffect, useRef } from 'react';
import { Animated, View, StyleSheet } from 'react-native';

interface ChartSkeletonProps {
  height?: number;
  /** barMode renders vertical bar placeholders instead of a line area */
  barMode?: boolean;
}

const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ height = 220, barMode = false }) => {
  const shimmer = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmer, { toValue: 1, duration: 900, useNativeDriver: true }),
        Animated.timing(shimmer, { toValue: 0, duration: 900, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmer]);

  const opacity = shimmer.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.7] });

  if (barMode) {
    const BAR_HEIGHTS = [60, 90, 50, 75, 40, 80];
    return (
      <Animated.View style={[styles.barContainer, { height, opacity }]}>
        {BAR_HEIGHTS.map((barH, i) => (
          <View key={i} style={styles.barTrack}>
            <View style={[styles.barFill, { width: `${(barH / 100) * 100}%` }]} />
          </View>
        ))}
      </Animated.View>
    );
  }

  return (
    <Animated.View style={[styles.lineContainer, { height, opacity }]}>
      {/* Y-axis ticks */}
      <View style={styles.yAxis}>
        {[0, 1, 2, 3].map((i) => (
          <View key={i} style={styles.yTick} />
        ))}
      </View>
      {/* Chart area */}
      <View style={styles.chartArea}>
        {/* Gridlines */}
        {[0, 1, 2, 3].map((i) => (
          <View key={i} style={[styles.gridLine, { bottom: `${(i / 3) * 85}%` as any }]} />
        ))}
        {/* Fake data points */}
        <View style={styles.pointsRow}>
          {[40, 55, 50, 65, 60].map((y, i) => (
            <View key={i} style={[styles.point, { marginBottom: y }]} />
          ))}
        </View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  lineContainer: {
    width: '100%',
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingVertical: 8,
    overflow: 'hidden',
  },
  yAxis: {
    width: 36,
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  yTick: {
    height: 10,
    width: 24,
    borderRadius: 4,
    backgroundColor: '#D1D5DB',
  },
  chartArea: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  gridLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: '#E5E7EB',
  },
  pointsRow: {
    position: 'absolute',
    bottom: 16,
    left: 8,
    right: 8,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-end',
  },
  point: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#C7D2E0',
  },
  barContainer: {
    width: '100%',
    justifyContent: 'space-around',
    paddingVertical: 4,
  },
  barTrack: {
    height: 20,
    width: '100%',
    backgroundColor: '#E5E7EB',
    borderRadius: 6,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    backgroundColor: '#D1D5DB',
    borderRadius: 6,
  },
});

export default ChartSkeleton;

